import { Component, inject, OnInit, Optional, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { CustomTableComponent } from 'src/app/components/custom-table/custom-table.component';
import { TypeMaintenanceSchedulingEnums } from 'src/app/enum/maintenance-scheduling.enums';
import { PreInvoicingStateEnums } from 'src/app/enum/pre-invoicing-state-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { MaintenanceSchedulingModel } from 'src/app/models/maintenance/maintenance-scheduling.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { MaintenanceSchedulingDetailService } from 'src/app/services/maintenance/maintenance-scheduling-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-process-maintenance-scheduling',
  templateUrl: './process-maintenance-scheduling.component.html',
  styleUrls: ['./process-maintenance-scheduling.component.scss'],
  standalone: false
})
export class ProcessMaintenanceSchedulingComponent implements OnInit {
  public config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly maintenanceSchedulingDetailService = inject(MaintenanceSchedulingDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  @Optional() private ref = inject(DynamicDialogRef);
  @ViewChild('customTable') customTable?: CustomTableComponent | null = null;

  ascendant: boolean = true;
  header: ColumnResponseModel[] = [];
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Agregar',
      icon: '',
      color: ActionColor.SUCCESS,
      action: 'add'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  currentStep: number = 1;
  dataArticle: PagedResponseModel<ArticleSelectionTableModel> = new PagedResponseModel<ArticleSelectionTableModel>();
  disabledStart: boolean = false;
  errorProcess: number = 0;
  listArticle: number[] = [];
  maintenanceSchedulingModel: MaintenanceSchedulingModel = new MaintenanceSchedulingModel();
  pageSize: number = 10;
  selectedItems: any[] = [];
  showPreinvoicingProgress: boolean = false;
  totalCount: number = 0;
  filters: ColumnResponseModel[] = [];
  limit: number = 10;
  loading: boolean = false;
  page: number = 1;
  processAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>();
  subscription: Subscription;
  visiblePdf: boolean = false;
  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.maintenanceSchedulingModel = inputs.maintenanceSchedulingModel;
    });
    this.loading = true;
    await this.loadDataForStep(1);
    setTimeout(() => this.loading = false, 300);
  }

  private clearFutureStepSelections(currentStep: number) {
    switch (currentStep) {
      case 1:
        this.visiblePdf = false;
        this.disabledStart = false;
        break;
    }
  }

  async downloadErrorsTxt() {
    if (!this.processAlerts || this.processAlerts.length === 0) {
      return;
    }

    this.maintenanceSchedulingDetailService.exportProcessErrorsTxt(this.processAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresProgramacionMantenimiento_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast('error', 'Error al exportar el archivo.');
      }
    });
  }

  async loadDataForStep(step: number) {
    try {
      switch (step) {
        case 1:
          await Promise.all([
            this.getColumnsForArticle(),
            this.getArticlesForSelection()
          ]);
          break;
      }
    } catch (error) {
      console.error(`Error cargando datos.`, error);
    }
  }

  async getColumnsForArticle() {
    try {
      this.header = await firstValueFrom(this.maintenanceSchedulingDetailService.getColumnsForArticles());
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getArticlesForSelection() {
    try {
      this.dataArticle = await firstValueFrom(this.maintenanceSchedulingDetailService.getAllArticles(this.maintenanceSchedulingModel.maintenanceSchedulingId, this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async goToStep(step: number, activateCallback: any) {
    if (step < this.currentStep) { this.clearFutureStepSelections(step); }
    if (!this.validateCurrentStepSelection(step)) { return; }
    await this.loadDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.currentStep) {
      case 1:
        await this.getArticlesForSelection();
        break;
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async processMaintenanceScheduling() {
    try {
      this.disabledStart = true;
      this.showPreinvoicingProgress = true;
      let service: Observable<LoadPreInvoicingModel>;
      if (this.maintenanceSchedulingModel.typeMaintenanceScheduling == TypeMaintenanceSchedulingEnums.horometro) {
        service = this.maintenanceSchedulingDetailService.processMaintenanceSchedulingByHourometer(this.maintenanceSchedulingModel.maintenanceSchedulingId, this.listArticle);
      }
      else {
        service = this.maintenanceSchedulingDetailService.processMaintenanceSchedulingByDays(this.maintenanceSchedulingModel.maintenanceSchedulingId, this.listArticle);
      }

      const response = await firstValueFrom(service);
      this.processAlerts = response.alerts;

      //Validacion para mostrar los mensajes de alerta y opción de exportarlos
      if (response.preInvoicingStateId == PreInvoicingStateEnums.error) {
        this.visiblePdf = true;
        this.toastHandlerService.showToast(
          'error',
          'El proceso ha terminado con Errores, expórtelos a PDF si lo desea.'
        );
      } else if (response.preInvoicingStateId == PreInvoicingStateEnums.alerta) {
        this.visiblePdf = true;
        this.toastHandlerService.showToast(
          'error',
          'El proceso ha terminado con Alertas, expórtelos a PDF si lo desea.'
        );
      } else {
        this.visiblePdf = false;
        this.toastHandlerService.showToast(
          'success',
          'El proceso ha terminado exitosamente.'
        );
      }
      this.onReloadEmit();
      this.errorProcess = response.preInvoicingStateId;
      this.showPreinvoicingProgress = false;

      this.clearSelectionTrigger++;
    } catch (error) {
      this.disabledStart = false;
      this.showPreinvoicingProgress = false;
      console.error('Error al procesar la programación', error);
    }

  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }


  async selectionChange(event: { selectedItem?: any }) {
    this.selectedItems = event?.selectedItem?.filter((item: any) => item.id !== null);
  }

  validateCurrentStepSelection(step: number): boolean {
    switch (step) {
      case 2:
        this.customTable?.returnSelectedData();
        if (this.selectedItems?.length == 0) {
          this.toastHandlerService.showToast('warning', 'Debe seleccionar al menos un equipo para procesar.', 'Atención');
          return false;
        }

        this.listArticle = this.selectedItems?.map(x => x.articleId);
        break;
    }
    return true;
  }
}
